import { useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { toast } from 'react-toastify';
import PageTitle from '@/components/titles/PageTitle';
import { Button } from '@/components/ui/button';
import useTranslation from '@/hooks/useTranslation';
import useOrganizationRoles from '@/hooks/useOrganizationRoles';
import {
  useGetOrgIntegrations,
  useStartColoopAuthorize,
  useDisconnectColoop,
} from '@/reactQuery/integrations';

/**
 * Every backend error code has a corresponding `msg.integrationsError_<code>`
 * key. Unknown codes fall through to `msg.integrationsError_unknown`
 * so a new backend error class the frontend hasn't been updated for
 * still lands the user somewhere friendly.
 */
const KNOWN_ERROR_CODES = new Set([
  'state_expired',
  'state_invalid',
  'org_mismatch',
  'user_mismatch',
  'pkce_missing',
  'not_configured',
  'coloop_unauthorized',
]);

function ColoopStatusBadge({ status }: { status?: string }) {
  if (!status) {
    return (
      <span className="text-xs px-2 py-1 rounded bg-muted text-muted-foreground">
        Not connected
      </span>
    );
  }
  const colorMap: Record<string, string> = {
    connected: 'bg-green-100 text-green-800 border-green-200',
    expired: 'bg-amber-100 text-amber-800 border-amber-200',
    revoked: 'bg-red-100 text-red-800 border-red-200',
  };
  return (
    <span className={`text-xs px-2 py-1 rounded border ${colorMap[status] ?? ''}`}>{status}</span>
  );
}

/**
 * Settings → Integrations page (TELV1-234).
 *
 * Lists every org-level integration the org has connected, with
 * Connect / Disconnect actions. Today CoLoop is the only provider; this
 * scaffold makes it easy to add Dovetail/Reduct/Notion later.
 */
function IntegrationsSettings() {
  const { lang } = useTranslation();
  const { organization_id: organizationId } = useParams<{ organization_id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: integrations, isLoading } = useGetOrgIntegrations(organizationId);
  const startAuthorize = useStartColoopAuthorize(organizationId);
  const disconnect = useDisconnectColoop(organizationId);
  const { isUserOwner, isUserAdmin } = useOrganizationRoles();
  // Connect / Disconnect / Reconnect are all mutations of the org's stored
  // credentials. Only OWNER/ADMIN can trigger them (backend enforces this
  // too). EDITORs see the status badge + attribution and nothing else —
  // they can't disconnect a working integration by accident.
  const canManage = isUserOwner || isUserAdmin;

  // Toast whichever error the OAuth callback bounced us back with, then
  // scrub the query param so a refresh doesn't re-toast.
  useEffect(() => {
    const errCode = searchParams.get('integration_error');
    if (!errCode) return;
    const key = KNOWN_ERROR_CODES.has(errCode)
      ? `msg.integrationsError_${errCode}`
      : 'msg.integrationsError_unknown';
    toast.error(lang.get(key));
    const next = new URLSearchParams(searchParams);
    next.delete('integration_error');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, lang]);

  const coloop = integrations?.find((i) => i.provider === 'coloop');
  const isConnected = coloop?.status === 'connected';
  const isExpired = coloop?.status === 'expired';
  const isRevoked = coloop?.status === 'revoked';
  const needsReconnect = isExpired || isRevoked;

  const handleConnect = async () => {
    const { authorizeUrl } = await startAuthorize.mutateAsync({
      redirectTarget: window.location.pathname,
    });
    window.location.href = authorizeUrl;
  };

  return (
    <main className="flex flex-col w-full h-full gap-6 p-6">
      <PageTitle title="integrations" />

      <section className="rounded-lg border bg-card p-6 max-w-3xl">
        <header className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold">CoLoop</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Push interview transcripts from any project in this organization to a CoLoop
              workspace. Connect once at the org level; choose which CoLoop project to push to per
              Tellet project.
            </p>
          </div>
          <ColoopStatusBadge status={coloop?.status} />
        </header>

        {isLoading ? (
          <div className="mt-6 flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading integration status…
          </div>
        ) : (
          <div className="mt-6 flex flex-wrap items-center gap-3">
            {canManage && !isConnected && !needsReconnect && (
              <Button onClick={handleConnect} disabled={startAuthorize.isPending} type="button">
                {startAuthorize.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Connect to CoLoop
              </Button>
            )}

            {canManage && needsReconnect && (
              <Button
                onClick={handleConnect}
                disabled={startAuthorize.isPending}
                variant="default"
                type="button"
              >
                {startAuthorize.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Reconnect to CoLoop
              </Button>
            )}

            {canManage && isConnected && (
              <Button
                onClick={() => disconnect.mutate()}
                disabled={disconnect.isPending}
                variant="destructive"
                type="button"
              >
                {disconnect.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Disconnect
              </Button>
            )}

            {isConnected && (
              <p className="text-xs text-muted-foreground">
                Connected by{' '}
                <strong>
                  {coloop?.lastConnectedByName ??
                    coloop?.createdByName ??
                    'a former admin'}
                </strong>
                {coloop?.lastConnectedAt && (
                  <>
                    {' '}on {new Date(coloop.lastConnectedAt).toLocaleDateString()}
                  </>
                )}
                . Pushes flow through this account&apos;s CoLoop identity.
              </p>
            )}

            {!canManage && !isConnected && (
              <p className="text-xs text-muted-foreground">
                Ask an org owner or admin to connect CoLoop for this
                organization.
              </p>
            )}

            {coloop?.metadata?.connectedEmail && (
              <span className="text-xs text-muted-foreground">
                Connected as {coloop.metadata.connectedEmail}
              </span>
            )}
          </div>
        )}

        {isExpired && (
          <p className="mt-4 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-3">
            CoLoop has revoked or expired the credentials. Reconnect to resume pushing transcripts.
            No project mappings are lost on reconnect.
          </p>
        )}

        {isRevoked && (
          <p className="mt-4 text-xs text-red-700 bg-red-50 border border-red-200 rounded p-3">
            CoLoop was disconnected for this organization. You previously had a working connection —
            click Reconnect to CoLoop above to sign in again. Any project links you had before
            disconnecting were cleared and will need to be re-linked.
          </p>
        )}
      </section>
    </main>
  );
}

export default IntegrationsSettings;
